/**
 * CreditNoteDialog — Sprint 1 Feb 2026, Factuur Crediteren.
 *
 * Toont een dialog waarin de gebruiker regels kan selecteren om te
 * crediteren. Roept `GET /admin/sales-invoices/{id}/credit-preview` aan om
 * de crediteerbare regels + resterend saldo op te halen, en verstuurt
 * daarna `POST /admin/sales-invoices/{id}/credit` met òf `full=true` òf
 * expliciete `lines[]`-selectie.
 *
 * Idempotency: bij openen genereren we een unieke `idempotency_key` zodat
 * dubbelklik/retry maximaal ÉÉN creditnota oplevert.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Checkbox } from "../../ui/checkbox";
import { Loader2, AlertTriangle, FileMinus2, ArrowRight } from "lucide-react";

import { adminApi } from "../../../context/AuthContext";

const _fmt = (n, cur = "EUR") => {
  const num = Number(n || 0);
  return `${cur} ${num.toLocaleString("nl-NL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const _newIdempotencyKey = () => {
  // Kort UUID-achtig, uniek genoeg voor idempotency binnen 1 dag.
  return `cn-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export default function CreditNoteDialog({
  open, onClose, invoice, onCredited,
}) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("full");  // "full" | "partial"
  const [selectedLines, setSelectedLines] = useState({});  // {idx: qty}
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");

  // ── Effects ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !invoice?.id) return;
    setIdempotencyKey(_newIdempotencyKey());
    setMode("full");
    setSelectedLines({});
    setReason("");
    setConfirmStep(false);
    setLoading(true);
    adminApi.get(`/admin/sales-invoices/${invoice.id}/credit-preview`)
      .then((r) => setPreview(r.data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Kan crediteren-preview niet laden");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [open, invoice?.id, onClose]);

  // ── Line-picker helpers ────────────────────────────────────────────
  const toggleLine = (idx, remaining) => {
    setSelectedLines((prev) => {
      const next = { ...prev };
      if (next[idx] !== undefined) delete next[idx];
      else next[idx] = remaining;
      return next;
    });
  };

  const setLineQty = (idx, val, max) => {
    const num = Math.max(0, Math.min(Number(val) || 0, max));
    setSelectedLines((prev) => ({ ...prev, [idx]: num }));
  };

  // ── Bereken preview van deze credit-selectie ───────────────────────
  const localTotals = useMemo(() => {
    if (!preview) return { excl: 0, vat: 0, incl: 0 };
    let excl = 0;
    let vat = 0;
    const lines = preview.creditable_lines || [];
    if (mode === "full") {
      for (const ln of lines) {
        const q = ln.remaining_creditable_quantity;
        const lineExcl = q * ln.unit_price;
        excl += lineExcl;
        vat += lineExcl * (ln.vat_percent / 100.0);
      }
    } else {
      for (const [idxStr, q] of Object.entries(selectedLines)) {
        const ln = lines[Number(idxStr)];
        if (!ln || q <= 0) continue;
        const lineExcl = q * ln.unit_price;
        excl += lineExcl;
        vat += lineExcl * (ln.vat_percent / 100.0);
      }
    }
    return {
      excl: -excl, vat: -vat, incl: -(excl + vat),
    };
  }, [preview, mode, selectedLines]);

  // ── Validation vóór confirmatie ────────────────────────────────────
  // Alleen "Selecteer minimaal 1 regel" tonen zodra de gebruiker
  // daadwerkelijk begonnen is met selecteren; anders is de melding een
  // onbedoelde "rode vlek" bij het openen van partial-mode.
  const validationError = useMemo(() => {
    if (!preview) return null;
    if (mode === "full") {
      if (preview.remaining_creditable_amount <= 0) {
        return "Deze factuur heeft geen resterend crediteerbaar bedrag.";
      }
      return null;
    }
    const chosen = Object.entries(selectedLines).filter(([, q]) => q > 0);
    if (chosen.length === 0) {
      // Geen selectie én ook geen aangeraakte state → geen zichtbare
      // foutmelding (de Verder-knop is al gedisabled via same check).
      const anyTouched = Object.keys(selectedLines).length > 0;
      if (!anyTouched) return null;
      return "Selecteer minimaal één regel met een aantal > 0.";
    }
    // Backend valideert alsnog per regel; frontend is best-effort feedback.
    for (const [idxStr, q] of chosen) {
      const ln = preview.creditable_lines[Number(idxStr)];
      if (!ln) continue;
      if (q > ln.remaining_creditable_quantity + 1e-6) {
        return `Regel ${Number(idxStr) + 1}: max ${ln.remaining_creditable_quantity} te crediteren.`;
      }
    }
    return null;
  }, [preview, mode, selectedLines]);

  // Verder-knop is gedisabled als er geen geldige selectie is
  const canProceed = useMemo(() => {
    if (!preview || preview.remaining_creditable_amount <= 0) return false;
    if (mode === "full") return true;
    const chosen = Object.entries(selectedLines).filter(([, q]) => q > 0);
    return chosen.length > 0 && !validationError;
  }, [preview, mode, selectedLines, validationError]);

  // ── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (submitting) return;  // extra guard tegen dubbelklik
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { reason, idempotency_key: idempotencyKey };
      if (mode === "full") {
        payload.full = true;
      } else {
        payload.lines = Object.entries(selectedLines)
          .filter(([, q]) => q > 0)
          .map(([idx, q]) => ({
            line_index: Number(idx), credit_quantity: q,
          }));
      }
      const r = await adminApi.post(
        `/admin/sales-invoices/${invoice.id}/credit`, payload,
      );
      toast.success(`Creditnota ${r.data.invoice_number} aangemaakt.`);
      if (onCredited) onCredited(r.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Crediteren mislukt");
    } finally {
      setSubmitting(false);
    }
  };

  const currency = preview?.currency || invoice?.currency || "EUR";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-3xl w-[calc(100%_-_1.5rem)] grid-cols-[minmax(0,1fr)] [&>*]:min-w-0 [overflow-wrap:anywhere] max-h-[90vh] overflow-y-auto" data-testid="credit-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#082c50]">
            <FileMinus2 className="w-5 h-5 text-red-600" />
            Factuur crediteren — {invoice?.invoice_number}
          </DialogTitle>
          <DialogDescription>
            Kies volledig of gedeeltelijk crediteren. De originele factuur
            blijft historisch intact; een aparte creditnota (CR-YYYY-NNNN)
            wordt aangemaakt.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
            <p className="text-sm text-gray-500 mt-2">Crediteren-preview laden…</p>
          </div>
        ) : !preview ? null : preview.remaining_creditable_amount <= 0 ? (
          <div className="py-4 flex items-start gap-3 border border-yellow-300 bg-yellow-50 p-3 rounded">
            <AlertTriangle className="w-5 h-5 text-yellow-700 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-bold text-yellow-900">Niets meer te crediteren</div>
              <div className="text-yellow-800">
                Deze factuur is al volledig gecrediteerd via eerdere creditnota&apos;s.
              </div>
              {(preview.existing_credit_notes || []).length > 0 && (
                <ul className="mt-2 text-xs">
                  {preview.existing_credit_notes.map((cn) => (
                    <li key={cn.id} className="font-mono">
                      {cn.credit_note_number} · {cn.date} · {_fmt(cn.total_incl_vat, currency)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : confirmStep ? (
          <ConfirmStep
            invoice={invoice}
            preview={preview}
            mode={mode}
            selectedLines={selectedLines}
            localTotals={localTotals}
            reason={reason}
            currency={currency}
          />
        ) : (
          <div className="space-y-4">
            {/* Origineel totaal + reeds gecrediteerd */}
            <div className="grid grid-cols-3 gap-2 border border-gray-200 p-3 bg-gray-50 text-xs">
              <div>
                <div className="text-[9px] uppercase font-bold text-gray-500">Origineel totaal</div>
                <div className="font-mono font-bold" data-testid="credit-preview-original">
                  {_fmt(preview.total_incl_vat, currency)}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase font-bold text-gray-500">Reeds gecrediteerd</div>
                <div className="font-mono font-bold text-red-700" data-testid="credit-preview-already">
                  {_fmt(preview.total_credited, currency)}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase font-bold text-gray-500">Nog crediteerbaar</div>
                <div className="font-mono font-bold text-emerald-700" data-testid="credit-preview-remaining">
                  {_fmt(preview.remaining_creditable_amount, currency)}
                </div>
              </div>
            </div>

            {/* Modus */}
            <RadioGroup value={mode} onValueChange={setMode} className="flex flex-wrap gap-4" data-testid="credit-mode">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="full" id="mode-full" data-testid="credit-mode-full" />
                <Label htmlFor="mode-full" className="cursor-pointer">Volledig crediteren</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="partial" id="mode-partial" data-testid="credit-mode-partial" />
                <Label htmlFor="mode-partial" className="cursor-pointer">Gedeeltelijk (regels kiezen)</Label>
              </div>
            </RadioGroup>

            {/* Regeltabel */}
            {mode === "partial" && (
              <div className="border border-gray-200">
                <table className="w-full text-xs block sm:table">
                  <thead className="bg-gray-50 uppercase text-[10px] font-bold text-gray-600 hidden sm:table-header-group">
                    <tr>
                      <th className="p-2 text-left w-8"></th>
                      <th className="p-2 text-left">Omschrijving</th>
                      <th className="p-2 text-right">Stuksprijs</th>
                      <th className="p-2 text-right">Origineel</th>
                      <th className="p-2 text-right">Al gecred.</th>
                      <th className="p-2 text-right">Rest</th>
                      <th className="p-2 text-right w-24">Crediteren</th>
                    </tr>
                  </thead>
                  <tbody className="block sm:table-row-group">
                    {preview.creditable_lines.map((ln) => {
                      const selected = selectedLines[ln.line_index] !== undefined;
                      const disabled = ln.remaining_creditable_quantity <= 0;
                      return (
                        <tr key={ln.line_index}
                            className={`border-t grid grid-cols-2 sm:table-row ${disabled ? "opacity-50" : ""}`}
                            data-testid={`credit-line-row-${ln.line_index}`}>
                          <td className="p-2">
                            <Checkbox
                              checked={selected}
                              disabled={disabled}
                              onCheckedChange={() => toggleLine(ln.line_index, ln.remaining_creditable_quantity)}
                              data-testid={`credit-line-check-${ln.line_index}`}
                            />
                          </td>
                          <td className="p-2 break-words min-w-0">{ln.description}</td>
                          <td className="p-2 text-right font-mono"><span className="sm:hidden">Prijs: </span>{_fmt(ln.unit_price, currency)}</td>
                          <td className="p-2 text-right font-mono"><span className="sm:hidden">Origineel: </span>{ln.original_quantity}</td>
                          <td className="p-2 text-right font-mono text-red-700"><span className="sm:hidden">Gecrediteerd: </span>{ln.already_credited_quantity}</td>
                          <td className="p-2 text-right font-mono text-emerald-700"><span className="sm:hidden">Rest: </span>{ln.remaining_creditable_quantity}</td>
                          <td className="p-2 text-right">
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              max={ln.remaining_creditable_quantity}
                              disabled={!selected || disabled}
                              value={selected ? selectedLines[ln.line_index] : ""}
                              onChange={(e) => setLineQty(
                                ln.line_index, e.target.value,
                                ln.remaining_creditable_quantity,
                              )}
                              className="h-7 text-xs text-right font-mono w-20 max-w-full"
                              data-testid={`credit-line-qty-${ln.line_index}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Nieuwe credit-totalen live preview */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 border border-red-200 bg-red-50 p-3 text-xs">
              <div>
                <div className="text-[9px] uppercase font-bold text-red-700">Subtotaal excl. BTW</div>
                <div className="font-mono font-bold text-red-900" data-testid="credit-total-excl">
                  {_fmt(localTotals.excl, currency)}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase font-bold text-red-700">BTW</div>
                <div className="font-mono font-bold text-red-900" data-testid="credit-total-vat">
                  {_fmt(localTotals.vat, currency)}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase font-bold text-red-700">Totaal creditnota</div>
                <div className="font-mono font-bold text-red-900 text-sm" data-testid="credit-total-incl">
                  {_fmt(localTotals.incl, currency)}
                </div>
              </div>
            </div>

            {/* Reden */}
            <div>
              <Label htmlFor="credit-reason" className="text-xs uppercase font-bold text-gray-600">
                Reden (optioneel, zichtbaar op creditnota)
              </Label>
              <Textarea
                id="credit-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                placeholder="Bijv. 'Klant heeft opdracht geannuleerd' of 'Foutieve BTW'"
                rows={2}
                className="text-xs"
                data-testid="credit-reason"
              />
            </div>

            {validationError && (
              <div className="text-xs text-red-700 bg-red-50 p-2 border border-red-200">
                <span data-testid="credit-validation-error">{validationError}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline" onClick={onClose} disabled={submitting}
            data-testid="credit-dialog-cancel"
          >
            Annuleren
          </Button>
          {preview && preview.remaining_creditable_amount > 0 && !loading && (
            confirmStep ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setConfirmStep(false)}
                  disabled={submitting}
                  data-testid="credit-dialog-back"
                >
                  Terug
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !!validationError}
                  className="bg-red-700 hover:bg-red-800 text-white"
                  data-testid="credit-dialog-confirm"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aanmaken…</>
                  ) : (
                    <>Creditnota aanmaken</>
                  )}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setConfirmStep(true)}
                disabled={!canProceed}
                className="bg-[#082c50] hover:bg-[#0a3763] text-white"
                data-testid="credit-dialog-next"
              >
                Verder <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ConfirmStep({
  invoice, preview, mode, selectedLines, localTotals, reason, currency,
}) {
  const chosenLines = mode === "full"
    ? (preview.creditable_lines || []).filter((ln) => ln.remaining_creditable_quantity > 0)
      .map((ln) => ({ ...ln, credit_quantity: ln.remaining_creditable_quantity }))
    : Object.entries(selectedLines)
      .filter(([, q]) => q > 0)
      .map(([idx, q]) => ({
        ...preview.creditable_lines[Number(idx)],
        credit_quantity: q,
      }));

  const newAmountDue = Math.max(
    0,
    (invoice.total_incl_vat || 0) - (invoice.total_paid || 0) -
    (preview.total_credited + Math.abs(localTotals.incl)),
  );

  return (
    <div className="space-y-3">
      <div className="border border-yellow-300 bg-yellow-50 p-3 text-sm">
        <div className="font-bold text-yellow-900 mb-1 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Bevestig deze creditnota
        </div>
        <div className="text-yellow-800 text-xs">
          Financiële actie — deze wijziging is permanent en wordt gelogd in
          het audit-trail. De originele factuur blijft historisch intact.
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="font-bold text-[10px] uppercase text-gray-500">Originele factuur</div>
          <div className="font-mono text-sm font-bold text-[#082c50]">{invoice.invoice_number}</div>
          <div className="mt-2 text-[10px] uppercase font-bold text-gray-500">Klant</div>
          <div>{invoice.customer_name}</div>
        </div>
        <div>
          <div className="font-bold text-[10px] uppercase text-gray-500">Nieuw resterend saldo</div>
          <div className="font-mono text-sm font-bold text-emerald-700" data-testid="credit-confirm-new-balance">
            {_fmt(newAmountDue, currency)}
          </div>
          <div className="mt-2 text-[10px] uppercase font-bold text-gray-500">Type</div>
          <div>{mode === "full" ? "Volledige credit" : "Gedeeltelijke credit"}</div>
        </div>
      </div>
      <div className="border border-gray-200">
        <table className="w-full text-xs block sm:table" data-testid="credit-confirm-lines">
          <thead className="bg-gray-50 uppercase text-[10px] font-bold text-gray-600 hidden sm:table-header-group">
            <tr>
              <th className="p-2 text-left">Omschrijving</th>
              <th className="p-2 text-right">Aantal</th>
              <th className="p-2 text-right">Stuksprijs</th>
              <th className="p-2 text-right">Regeltotaal</th>
            </tr>
          </thead>
          <tbody className="block sm:table-row-group">
            {chosenLines.map((ln, idx) => (
              <tr key={idx} className="border-t grid grid-cols-2 sm:table-row" data-testid={`credit-confirm-line-${idx}`}>
                <td className="p-2 col-span-2 min-w-0 [overflow-wrap:anywhere]" data-testid={`credit-confirm-description-${idx}`}>{ln.description}</td>
                <td className="p-2 text-right font-mono text-red-700" data-testid={`credit-confirm-quantity-${idx}`}><span className="sm:hidden">Aantal: </span>-{ln.credit_quantity}</td>
                <td className="p-2 text-right font-mono" data-testid={`credit-confirm-price-${idx}`}><span className="sm:hidden">Stuksprijs: </span>{_fmt(ln.unit_price, currency)}</td>
                <td className="p-2 col-span-2 text-right font-mono text-red-700" data-testid={`credit-confirm-amount-${idx}`}>
                  <span className="sm:hidden">Regeltotaal: </span>
                  {_fmt(-ln.credit_quantity * ln.unit_price, currency)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 font-bold text-xs block sm:table-footer-group">
            <tr className="bg-gray-50 flex flex-wrap justify-between sm:table-row">
              <td colSpan={3} className="p-2 text-right">Subtotaal excl. BTW</td>
              <td className="p-2 text-right font-mono text-red-900" data-testid="credit-confirm-total-excl">{_fmt(localTotals.excl, currency)}</td>
            </tr>
            <tr className="flex flex-wrap justify-between sm:table-row">
              <td colSpan={3} className="p-2 text-right">BTW</td>
              <td className="p-2 text-right font-mono text-red-900" data-testid="credit-confirm-total-vat">{_fmt(localTotals.vat, currency)}</td>
            </tr>
            <tr className="flex flex-wrap justify-between sm:table-row">
              <td colSpan={3} className="p-2 text-right">Totaal creditnota</td>
              <td className="p-2 text-right font-mono text-red-900 text-sm" data-testid="credit-confirm-total-incl">
                {_fmt(localTotals.incl, currency)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {reason && (
        <div className="text-xs">
          <div className="font-bold text-[10px] uppercase text-gray-500">Reden</div>
          <div className="italic text-gray-700">{reason}</div>
        </div>
      )}
    </div>
  );
}
